import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SECTIONS } from "@/lib/sections";
import { ArticleView } from "@/pages/ArticlePage";
import { ImageUpload } from "@/pages/admin/ImageUpload";
import { EditorActions } from "@/pages/admin/EditorActions";
import { Switch } from "@/components/ui/switch";
import { SeriesFields } from "@/pages/admin/SeriesFields";
import { CorrectionsFields } from "@/pages/admin/CorrectionsFields";
import { formatDateTime } from "@/lib/api";

const EMPTY = { headline: "", standfirst: "", section: "uk-news", interview: false, content_note: "", image_path: "", body: "", status: "draft", series: "", series_part: null, audio_url: "", scheduled_for: null, corrections: [] };
const inputCls = "w-full border border-[#CFCFCB] bg-white px-3 py-2.5 font-ui text-sm outline-none focus:border-[var(--ink)] transition-colors";
const Label = ({ children, htmlFor }) => <label htmlFor={htmlFor} className="block meta !text-[var(--ink-2)] mb-2">{children}</label>;

export default function AdminEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(id ? null : EMPTY);
  const [saved, setSaved] = useState(null);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (id) api.get(`/admin/articles/${id}`).then((r) => { setForm(r.data); setSaved(r.data); });
  }, [id]);

  if (!form) return <p className="meta">Loading</p>;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const save = async (status, extra = {}) => {
    if (!form.headline.trim()) return toast.error("A headline is required");
    setBusy(true);
    try {
      const payload = { ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k]])), status, ...extra };
      payload.corrections = (payload.corrections || []).filter((c) => c.text.trim());
      const { data } = id ? await api.put(`/admin/articles/${id}`, payload) : await api.post("/admin/articles", payload);
      setForm(data); setSaved(data);
      toast.success({ published: "Published", scheduled: `Scheduled for ${formatDateTime(data.published_at)}`, draft: "Saved as draft" }[status]);
      if (!id) nav(`/admin/edit/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    await api.delete(`/admin/articles/${id}`);
    toast.success("Article deleted");
    nav("/admin");
  };

  const sendToSubscribers = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/articles/${id}/send`);
      toast.success(`Sent to ${data.sent} subscriber${data.sent === 1 ? "" : "s"}${data.failed ? ` (${data.failed} failed)` : ""}`);
      const r = await api.get(`/admin/articles/${id}`);
      setSaved(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-editor">
      <EditorActions isNew={!id} saved={saved} busy={busy} preview={preview} setPreview={setPreview} onSave={save} onDelete={remove} onSend={sendToSubscribers} />
      {preview ? (
        <div className="bg-[var(--paper)] border border-[var(--rule)] py-14 px-5" data-testid="admin-preview-pane"><ArticleView a={{ ...form, published_at: form.published_at || new Date().toISOString() }} /></div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
          <div className="bg-white border border-[var(--rule)] p-6 sm:p-8 grid gap-6">
            <textarea rows={2} value={form.headline} onChange={set("headline")} placeholder="Headline" className="w-full resize-none font-display text-3xl sm:text-4xl font-bold outline-none placeholder:text-[#C8C8C4]" data-testid="admin-headline-input" />
            <textarea rows={2} value={form.standfirst} onChange={set("standfirst")} placeholder="Standfirst — a short line introducing the piece" className="w-full resize-none font-display italic text-xl text-[var(--ink-2)] outline-none border-l-2 border-[var(--rule)] pl-4 placeholder:text-[#C8C8C4]" data-testid="admin-standfirst-input" />
            <div>
              <Label htmlFor="body">Body (Markdown: ## subheading, &gt; quote, **bold**)</Label>
              <textarea id="body" rows={22} value={form.body} onChange={set("body")} className={`${inputCls} font-mono-ui text-[13px] leading-relaxed`} data-testid="admin-body-markdown-input" />
            </div>
          </div>
          <aside className="grid gap-6 content-start bg-white border border-[var(--rule)] p-6">
            <div>
              <Label htmlFor="section">Section</Label>
              <select id="section" value={form.section} onChange={set("section")} className={inputCls} data-testid="admin-section-select">
                {SECTIONS.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div><p className="font-ui text-sm font-medium">In their words</p><p className="font-ui text-xs text-[var(--ink-3)]">Mark as an interview piece</p></div>
              <Switch checked={form.interview} onCheckedChange={set("interview")} data-testid="admin-interview-checkbox" />
            </div>
            <div>
              <Label htmlFor="note">Content note (optional)</Label>
              <input id="note" value={form.content_note} onChange={set("content_note")} placeholder="e.g. Contains descriptions of violence" className={inputCls} data-testid="admin-content-note-input" />
            </div>
            <div><Label>Lead image</Label><ImageUpload value={form.image_path} onChange={set("image_path")} /></div>
            <SeriesFields form={form} set={set} inputCls={inputCls} />
            <div>
              <Label htmlFor="audio">Audio link (optional)</Label>
              <input id="audio" value={form.audio_url || ""} onChange={set("audio_url")} placeholder="https://soundcloud.com/…" className={inputCls} data-testid="admin-audio-url-input" />
              <p className="mt-1.5 font-ui text-xs text-[var(--ink-3)]">SoundCloud, Spotify or a direct .mp3/.m4a link.</p>
            </div>
            <CorrectionsFields value={form.corrections || []} onChange={set("corrections")} inputCls={inputCls} />
          </aside>
        </div>
      )}
    </div>
  );
}
